const ATTEMPT_ID_QUERY_PARAM = 'bmaAttemptId';
// Ghost Admin builds the ID as `${Date.now()}-${attemptSequence}`.
const ATTEMPT_ID_PATTERN = /^\d{1,16}-\d{1,8}$/;

/**
 * @typedef {Parameters<NonNullable<import('@sentry/react').BrowserOptions['beforeSend']>>[0]} SentryErrorEvent
 */

/**
 * Reads the Admin attempt ID from the URL the iframe document was loaded with.
 * Read once at module load so the ID survives client-side navigation.
 *
 * @returns {string|null} The attempt ID, or null when missing or malformed
 */
function readAttemptId() {
    const queryId = new URLSearchParams(window.location.search).get(ATTEMPT_ID_QUERY_PARAM);
    return ATTEMPT_ID_PATTERN.test(queryId || '') ? queryId : null;
}

const attemptId = readAttemptId();
const attemptStartedAt = attemptId ? Number(attemptId.split('-')[0]) : null;
/** @type {number|null} */
let readySignalAttemptedAt = null;

/**
 * Records when Billing posted its readiness message to Admin.
 * Posting does not confirm that Admin received it.
 *
 * @param {number} sentAt - Timestamp included in the readiness message
 * @returns {void}
 */
export function recordBillingReady(sentAt) {
    readySignalAttemptedAt = sentAt;
}

/**
 * Enriches existing events and request headers with iframe attempt
 * context, without adding captures or changing requests.
 *
 * @param {SentryErrorEvent} event - Sentry error event
 * @returns {SentryErrorEvent} The enriched event
 */
export function addBillingDiagnostics(event) {
    return {
        ...event,
        tags: {
            ...event.tags,
            ...(attemptId ? { billing_attempt_id: attemptId } : {}),
        },
        contexts: {
            ...event.contexts,
            billing_iframe: {
                attempt_id: attemptId,
                attempt_age_ms: attemptStartedAt === null ? null : Date.now() - attemptStartedAt,
                ready_signal_attempted_at: readySignalAttemptedAt,
                document_visibility_state: document.visibilityState,
                navigator_online: navigator.onLine,
            },
        },
    };
}
